import React, { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppContext } from '../../../App'
import AddAlertIcon from '../../../assets/icons/AddAlertIcon'
import { ExtraSmallButton } from '../../custom'
import { setAlertModalData } from '../../../reducer/alerts.reducer'
import {
    buildAlertDrawingFromChartDrawing,
    findUserAlertForDrawingId,
    getUserAlertMessageFromCondition,
    resolveDrawingAlertModalPayload,
} from '../../../util/alerts'
import { selectIndicators } from '../../../selectors/chart'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../adminUserRestrictions/Restrictions.constants'
import { FeatureCode } from '../../../util/RestrictionFeatureCode'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { RootState } from '../../../store'

type DrawingAlertButtonProps = {
    drawingId: string
    chartId: string
    drawingType: string
    symbolName?: string
}

const DrawingAlertButton: React.FC<DrawingAlertButtonProps> = ({
    drawingId,
    chartId,
    drawingType,
    symbolName,
}) => {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const indicators = useSelector(selectIndicators)
    const alerts = useSelector((state: RootState) => state.alertsStore.alerts)
    const { user: { hasAccess } } = useUser()
    const hasExistingAlert = Boolean(findUserAlertForDrawingId(alerts, drawingId))

    const handleClick = useCallback(async () => {
        if (!hasAccess(FeatureCategory.CHART, FeatureSubCategory.ALERTS)) {
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: 'Alerts',
                group: Group.CHART,
                featureRestrictionCode: FeatureCode.CHART_ALERTS,
            }))
            return
        }

        const chart = appContext.getChart(chartId)
        if (!chart) return

        const drawing = buildAlertDrawingFromChartDrawing(chart, drawingId)
        if (!drawing) return

        const getSnapshot = appContext.getChartSnapshot?.[chartId]
        const snapshot = getSnapshot ? getSnapshot() : null
        if (!snapshot) return

        AnalyticsManager.logEvent(
            findUserAlertForDrawingId(alerts, drawingId) ? 'Edit Drawing Alert Clicked' : 'Add Drawing Alert Clicked',
            { source: 'drawing-toolbar', drawingType }
        )

        const { alert, edit, sourceDrawing } = resolveDrawingAlertModalPayload(alerts, drawing, snapshot)
        const primary = snapshot.securityList?.[0]
        let message = alert.message
        if (!edit) {
            message = (await getUserAlertMessageFromCondition(
                alert.condition,
                symbolName || primary?.code || '',
                indicators,
                {
                    chartSnapshot: snapshot,
                    chartTimeBucket: snapshot.timeBucket ?? alert.timeBucket,
                }
            )) || alert.message
        }

        dispatch(setAlertModalData({
            chartSnapshot: snapshot,
            alert: {
                ...alert,
                security: primary
                    ? { exchange: primary.exchange, code: primary.code }
                    : alert.security,
                message,
            },
            edit,
            sourceDrawing,
        }))
    }, [alerts, appContext, chartId, drawingId, drawingType, dispatch, hasAccess, indicators, symbolName])

    return (
        <ExtraSmallButton
            onClick={handleClick}
            color="inherit"
            title={hasExistingAlert ? 'Edit alert on drawing' : 'Add alert on drawing'}
        >
            <AddAlertIcon sx={{ fontSize: '1.25rem !important' }} />
        </ExtraSmallButton>
    )
}

export default React.memo(DrawingAlertButton)
